// Generic
import HorizontalGridLabels from "../Pulsar/components/HorizontalGridLabels";
import HorizontalGrid from "../Pulsar/components/HorizontalGrid";
import FilterContainer from "../Pulsar/components/Filters/FilterContainer";

// // Bases
import dnaOverlay from "../Pulsar/tracks/dna/DnaOverlay";
import dnaTrackController from "../Pulsar/tracks/dna/dnaTrackController";

// conservation
import conservationTrackController from "../Pulsar/tracks/conservation/conservationTrackController";
import ConservationTitle from "../Pulsar/tracks/conservation/components/ConservationTitle";
import ConservationTooltip from "../Pulsar/tracks/conservation/components/ConservationTooltip";

// // Transcripts
import transcriptTrackController from "../Pulsar/tracks/transcripts/transcriptTrackController";
import TranscriptTooltip from "../Pulsar/tracks/transcripts/components/TranscriptTooltip";
import TranscriptsOverlay from "../Pulsar/tracks/transcripts/components/TranscriptsOverlay";
import TranscriptInfo from "../Pulsar/tracks/transcripts/components/TranscriptInfo";

// // pathogenicity
import sampleTrackController from "../Pulsar/tracks/sample/sampleTrackController";
import PetalTitle from "../Pulsar/tracks/sample/components/PetalTitle";
import PetalTooltip from "../Pulsar/tracks/sample/components/PetalTooltip";
import processVariantResponse from "../Pulsar/tracks/sample/processResponse";
import processCnvResponse from "../Pulsar/tracks/cnv/processResponse";

// Variants
import getApiClient from "../Pulsar/apiClient";
import selectionTrackController from "../Pulsar/tracks/selection/selectionTrackController";

// structural variants
import CnvTitle from "../Pulsar/tracks/cnv/components/CnvTitle";
import CnvTooltip from "../Pulsar/tracks/cnv/components/CnvTooltip";
import cnvController from "../Pulsar/tracks/cnv/cnvController";
import RohTitle from "../Pulsar/tracks/roh/components/RohTitle";
import RohTooltip from "../Pulsar/tracks/roh/components/RohTooltip";
import rohTrackController from "../Pulsar/tracks/roh/rohTrackController";

function getTracks(allSamples, sample, maxRange) {
  const api = getApiClient();
  const { getAnalysisVariants, getAnalysisCnvs, getRohBedFile } = api;

  // maxRange
  const isAffected = Number(sample?.affectedStatus) === 1;
  const affectedTxt = isAffected ? "- [Affected]" : "";
  const snvsApiFn = getAnalysisVariants(processVariantResponse);
  const cnvApiFn = getAnalysisCnvs(processCnvResponse);
  const rohTrack = {
    id: `roh-sample-${sample.name}}`,
    title: `Runs of Homozygosity in ${sample.name}`,
    contentHeight: 24,
    pTop: 2,
    pBottom: 2,
    TitleRenderer: RohTitle,
    TooltipRenderer: RohTooltip,
    controller: rohTrackController,
    data: { sample, allSamples },
    apiLoader: { apiFn: getRohBedFile, maxRange: Infinity },
  };
  const variantTrack = {
    id: `variants-sample-${sample.name}`,
    title: `Variants in ${sample.name} ${affectedTxt}`,
    contentHeight: 150,
    trackType: "variants-sample",
    pTop: 40,
    pBottom: 40,
    TitleRenderer: PetalTitle,
    TooltipRenderer: PetalTooltip,
    AxisRenderer: HorizontalGridLabels,
    GridRenderer: HorizontalGrid,
    FilterRenderer: FilterContainer,
    controller: sampleTrackController,
    data: { sample, allSamples },
    apiLoader: { apiFn: snvsApiFn, maxRange },
  };

  const cnvTrack = {
    id: `cnvs-sample-${sample.name}`,
    title: `SVs in ${sample.name} ${affectedTxt}`,
    contentHeight: 0,
    pTop: 8,
    pBottom: 8,
    FilterRenderer: FilterContainer,
    TitleRenderer: CnvTitle,
    TooltipRenderer: CnvTooltip,
    controller: cnvController,
    data: { sample, allSamples },
    apiLoader: { apiFn: cnvApiFn, maxRange: Infinity },
  };

  const tracks =
    sample?.hasCnv || sample?.hasRepExp
      ? [rohTrack, variantTrack, cnvTrack]
      : [rohTrack, variantTrack];

  return tracks.filter(Boolean);
}

function getSampleBrowserTracks({ info, samples: sampleArr }) {
  const { loadMainTranscripts, loadSequence, loadConservation } =
    getApiClient();

  const variantCount = info?.results_count || Infinity;
  const maxDnaRange = 10000;
  const maxConservationRange = 300000;
  const restrictRange = variantCount > 1000000;
  const maxSNVRange = restrictRange ? 10000000 : Infinity;

  const samples = (sampleArr || []).slice(0, 10).map((x) => ({
    name: x.sample_name,
    id: x.sample_id,
    hasCnv: x.has_cnv_analyses,
    hasRepExp: x.has_re_analyses,
    affectedStatus: x.affected_status,
  }));

  const sampleTracks = samples
    .map((x) => getTracks(samples, x, maxSNVRange))
    .reduce((a, i) => a.concat(i), []);

  const tracks = [
    {
      id: "dna-sample",
      title: "Dna",
      contentHeight: 25,
      pTop: 0,
      pBottom: 0,
      headerHeight: 0,
      isStickyTop: true,
      OverlayRenderer: dnaOverlay,
      controller: dnaTrackController,
      apiLoader: {
        apiFn: loadSequence,
        maxRange: maxDnaRange,
      },
    },
    {
      id: "overlay-sample",
      title: "overlay",
      headerHeight: 0,
      isOverlay: true,
      contentHeight: 1,
      pTop: 0,
      pBottom: 0,
      controller: selectionTrackController,
    },
    {
      id: "conservation-sample",
      title: "Conservation Scores",
      contentHeight: 50,
      pTop: 2,
      pBottom: 2,
      TitleRenderer: ConservationTitle,
      TooltipRenderer: ConservationTooltip,
      controller: conservationTrackController,
      apiLoader: {
        apiFn: loadConservation,
        maxRange: maxConservationRange,
      },
    },
    {
      id: "transcript-sample",
      title: "Transcripts",
      contentHeight: 0,
      pTop: 8,
      pBottom: 8,
      FilterRenderer: FilterContainer,
      TooltipRenderer: TranscriptTooltip,
      TitleRenderer: TranscriptInfo,
      OverlayRenderer: TranscriptsOverlay,
      controller: transcriptTrackController,
      apiLoader: {
        apiFn: loadMainTranscripts,
        maxRange: Infinity,
      },
    },
    ...sampleTracks,
    {
      id: "sample-btm",
      contentHeight: 400,
      headerHeight: 0,
      controller: () => ({ onReset: () => {}, meshes: [] }),
    },
  ];

  return tracks;
}

export default getSampleBrowserTracks;
